import * as React from 'react'
import type {
  GridColumnVisibilityModel,
  GridValueFormatterParams,
} from '@mui/x-data-grid-pro'
import {
  ageValueFormatter,
  branchValueFormatter,
  bulkErrorColumn,
  campaignValueFormatter,
  GridEmailLink,
  GridLocationLink,
  GridPhoneLink,
  salesFunnelStageValueFormatter,
  salesFunnelValueFormatter,
  salesTeamValueFormatter,
  SortConfig,
  sourceValueFormatter,
  userValueFormatter,
} from '@user/components/xGrid'

import Link from '@mui/material/Link'
import { LocationsListViewWrapper } from '@user/components/EntityCards/Locations'
import { referralDiscountCodeFormatter } from '@user/components/xGrid/valueFormatters/referralDiscountCode'
import {
  ENTITY_TYPES,
  getFormattedAddress,
  IServiceDTO,
  IUser,
} from '@sales-center/common'
import {
  currencyValueFormatter,
  divisionValueFormatter,
  locationTypeValueFormatter,
} from '@user/components/EntityCards/Locations/valueFormaters'
import { isMatchedEntity } from '@user/utils/isMatchEntity'
import { soldServiceStatusValueFormatter } from '@user/components/xGrid/valueFormatters/soldServiceStatusValueFormatter'
import { referralValueChecker } from '@user/components/xGrid/valueFormatters/referralValueFormatter'
import { ITargetPest } from '@user/store/targetPests'
import ListViewAddressMapLink from '@user/components/Leads/LeadList/ListViewAddressMapLink'
import Typography from '@mui/material/Typography'

export const opportunityListColumns = (
  allTargetPests: ITargetPest[],
  supportLInks: boolean = true,
  knockStatusEnabled: boolean = false,
) => {
  return [
    { ...bulkErrorColumn },
    {
      field: 'createdDate',
      headerName: 'Opportunity Created Date',
      type: 'date',
      valueGetter: (params) => params.row.createdDate,
      width: 145,
    },
    {
      field: 'leadCreatedTime',
      headerName: 'Lead Created Date',
      type: 'date',
      valueGetter: (params) => params.row.leadCreatedTime,
      width: 145,
    },
    {
      field: 'age',
      headerName: 'Age',
      valueFormatter: ageValueFormatter,
    },
    {
      field: 'contactWithOpportunityName',
      headerName: 'Opportunity Name',
      renderCell: ({ value, row }) =>
        supportLInks ? (
          <Link
            href={`#/opportunities/${row.id}`}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {value}
          </Link>
        ) : (
          <Typography>{value}</Typography>
        ),
      width: 150,
    },
    {
      field: 'externalBillToCode',
      headerName: 'Bill-to Location Number',
      width: 170,
      renderCell: ({ row }) => {
        return (
          row.externalBillToId && (
            <GridLocationLink
              locationId={row.externalBillToId}
              locationCode={row.externalBillToCode}
              variant="billto"
            />
          )
        )
      },
    },
    {
      field: 'externalLocationCode',
      headerName: 'Service Location Number',
      width: 170,
      renderCell: ({ row }) => {
        return row.serviceLocationsCount > 1 ? (
          isMatchedEntity(row.externalLocationId, row.externalLocationCode) && (
            <LocationsListViewWrapper
              field="externalLocationCode"
              row={row}
              entityType={ENTITY_TYPES.Opportunity}
            />
          )
        ) : (
          <GridLocationLink
            locationId={row.externalLocationId}
            locationCode={row.externalLocationCode}
            variant="service"
          />
        )
      },
    },

    {
      field: 'divisionId',
      headerName: 'Service Location Division',
      width: 170,
      renderCell: ({ row }) =>
        supportLInks && row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="divisionId"
            row={row}
            entityType={ENTITY_TYPES.Opportunity}
          />
        ) : (
          divisionValueFormatter(row.serviceLocation.divisionId)
        ),
    },

    {
      field: 'locationTypeId',
      headerName: 'Service Location Type',
      width: 170,
      renderCell: ({ row }) =>
        supportLInks && row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="locationTypeId"
            row={row}
            entityType={ENTITY_TYPES.Opportunity}
          />
        ) : (
          locationTypeValueFormatter(row.serviceLocation.locationTypeId)
        ),
    },
    {
      field: 'nextActionDate',
      headerName: 'Next Action Date',
      type: 'date',
      width: 165,
    },
    {
      field: 'expectedClosingDate',
      headerName: 'Expected Closing Date',
      type: 'date',
      width: 195,
    },
    {
      field: 'assignee/firstName',
      headerName: 'Owner',
      valueGetter: ({ row }) => row.assignee,
      valueFormatter: (params: GridValueFormatterParams) =>
        userValueFormatter(params),
      width: 110,
    },
    {
      field: 'primaryContactSearchAddress',
      headerName: 'Primary Contact Address',
      renderCell: ({ row }) =>
        getFormattedAddress(
          row.primaryContact.address,
          row.primaryContact.address2,
          row.primaryContact.city,
          row.primaryContact.state,
          row.primaryContact.postalCode,
        ),
      width: 130,
      sortable: false,
    },
    {
      field: 'primaryContactSearchPrimaryEmail',
      headerName: 'Primary Contact Email',
      renderCell: ({ row }) => (
        <GridEmailLink value={row.primaryContact.primaryEmailAddress} />
      ),
      width: 130,
      sortable: false,
    },
    {
      field: 'primaryContactSearchPhone',
      headerName: 'Primary Contact Phone',
      renderCell: ({ row }) => (
        <GridPhoneLink
          phone={row.primaryContact.phone}
          ext={row.primaryContact.phoneExt}
        />
      ),
      width: 130,
      sortable: false,
    },
    {
      field: 'salesFunnelId',
      headerName: 'Sales Funnel',
      valueFormatter: salesFunnelValueFormatter,
      width: 145,
    },
    {
      field: 'address',
      headerName: 'Service Location Address',
      width: 350,
      sortable: false,
      renderCell: ({ row }) => {
        if (!supportLInks) {
          const formattedAddress = getFormattedAddress(
            row.serviceLocation.lineOne,
            row.serviceLocation.lineTwo,
            row.serviceLocation.city,
            row.serviceLocation.state,
            row.serviceLocation.postalCode,
          )

          return <Typography>{formattedAddress}</Typography>
        }

        return row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="address"
            row={row}
            entityType={ENTITY_TYPES.Opportunity}
          />
        ) : (
          <ListViewAddressMapLink
            row={{ ...row, entityType: ENTITY_TYPES.Opportunity }}
          />
        )
      },
    },
    {
      field: 'salesFunnelStageId',
      headerName: 'Stage',
      valueFormatter: salesFunnelStageValueFormatter,
      width: 110,
    },
    {
      field: 'totalValue',
      headerName: 'Total Value',
      valueFormatter: currencyValueFormatter,
      width: 140,
      renderCell: ({ row }) =>
        supportLInks && row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="totalValue"
            row={row}
            listViewValue={currencyValueFormatter(row.totalValue)}
            entityType={ENTITY_TYPES.Opportunity}
          />
        ) : (
          currencyValueFormatter(row.totalValue)
        ),
    },
    {
      field: 'sourceId',
      headerName: 'Lead Source',
      valueFormatter: sourceValueFormatter,
      width: 140,
    },
    {
      field: 'campaignId',
      headerName: 'Campaign',
      valueFormatter: campaignValueFormatter,
      width: 130,
    },
    {
      field: 'billToDivisionId',
      headerName: 'Bill-to Division',
      width: 120,
      renderCell: ({ row }) => divisionValueFormatter(row.billToDivisionId),
    },
    {
      field: 'billToLocationTypeId',
      headerName: 'Bill-to Location Type',
      width: 150,
      renderCell: ({ row }) =>
        locationTypeValueFormatter(row.billToLocationTypeId),
    },
    {
      field: 'leadCreatedBy/firstName',
      headerName: 'Lead Created By',
      valueGetter: ({ row }) => row.leadCreatedBy,
      valueFormatter: (row: GridValueFormatterParams) => {
        const value: IUser = row?.value as IUser
        if (value) {
          if (value.id === '00000000-0000-0000-0000-000000000000') return ''
          return `${value.firstName} ${value.lastName}`
        }
      },
      width: 130,
    },
    {
      field: 'createdBy/firstName',
      headerName: 'Opportunity Created By',
      valueGetter: ({ row }) => row.createdBy,
      valueFormatter: userValueFormatter,
      width: 130,
    },
    {
      field: 'services',
      headerName: 'Services',
      valueFormatter: ({ value }) =>
        (value as IServiceDTO[])?.map(({ name }) => name).join(', '),
      sortable: false,
    },
    {
      field: 'salesTeamId',
      headerName: 'Sales Team',
      valueFormatter: salesTeamValueFormatter,
      width: 140,
    },
    {
      field: 'branchId',
      headerName: 'Branch',
      valueFormatter: branchValueFormatter,
      width: 120,
    },
    {
      field: 'initialValue',
      headerName: 'Initial Value',
      width: 110,
      renderCell: ({ row }) =>
        supportLInks && row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="initialValue"
            row={row}
            listViewValue={currencyValueFormatter(row.initialValue)}
            entityType={ENTITY_TYPES.Opportunity}
          />
        ) : (
          currencyValueFormatter(row.initialValue)
        ),
    },
    {
      field: 'recurringValue',
      headerName: 'Annual Value',
      width: 140,
      renderCell: ({ row }) =>
        supportLInks && row.serviceLocationsCount > 1 ? (
          <LocationsListViewWrapper
            field="recurringValue"
            row={row}
            listViewValue={currencyValueFormatter(row.recurringValue)}
            entityType={ENTITY_TYPES.Opportunity}
          />
        ) : (
          currencyValueFormatter(row.recurringValue)
        ),
    },
    {
      field: 'referralDiscountCode',
      headerName: 'Referral Discount Code',
      valueFormatter: referralDiscountCodeFormatter,
      width: 110,
    },
    {
      field: 'closedDate',
      headerName: 'Closed Date',
      type: 'date',
      width: 165,
    },
    {
      field: 'primaryContactSearchName',
      headerName: 'Primary Contact Name',
      width: 130,
      sortable: false,
    },
    {
      field: 'soldServiceStatus',
      headerName: 'Closed/Won Status',
      width: 130,
      sortable: true,
      valueFormatter: soldServiceStatusValueFormatter,
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 110,
    },
    {
      field: 'closedLostReason',
      headerName: 'Closed/Lost Reason',
      width: 170,
    },
    {
      field: 'targetPestIds',
      headerName: 'Target Pests',
      sortable: false,
      valueFormatter: ({ value }) => {
        return value
          .map((id) => allTargetPests?.find((pest) => pest.id === id)?.name)
          .filter(Boolean)
          .join(', ')
      },
      width: 170,
    },
    {
      field: 'submittedBy',
      headerName: 'Submitted by',
      width: 170,
    },
    {
      field: 'billToAddress',
      headerName: 'Bill-To Address',
      width: 170,
      renderCell: ({ row }) => {
        const formattedAddress = getFormattedAddress(
          row.billToContact.street1,
          row.billToContact.street2,
          row.billToContact.city,
          row.billToContact.stateAbbreviation,
          row.billToContact.postalCode,
        )

        return <Typography>{formattedAddress}</Typography>
      },
    },
    {
      field: 'customerReferralLocationCode',
      headerName: 'Customer Referral',
      width: 150,
      renderCell: ({ row }) => {
        const {
          referralType,
          customerReferralLocationCode,
          customerReferralLocationId,
        } = row

        const checkedValue = referralValueChecker(
          referralType,
          'Customer',
          customerReferralLocationCode,
        )
        if (!checkedValue || !supportLInks) return checkedValue

        return (
          <GridLocationLink
            locationId={customerReferralLocationId}
            locationCode={checkedValue}
            variant="billto"
          />
        )
      },
    },
    {
      field: 'employeeReferral',
      headerName: 'Employee Referral',
      width: 150,
      renderCell: ({ row }) => {
        const { referralType, employeeReferral } = row
        return referralValueChecker(referralType, 'Employee', employeeReferral)
      },
    },
    {
      field: 'otherReferral',
      headerName: 'Other Referral',
      width: 150,
      renderCell: ({ row }) => {
        const { referralType, otherReferral } = row
        return referralValueChecker(referralType, 'Other', otherReferral)
      },
    },
    ...(knockStatusEnabled
      ? [
          {
            field: 'knockStatus',
            headerName: 'Current Knock Status',
            width: 200,
          },
          {
            field: 'knockStatusReason',
            headerName: 'Knock Status Reason',
            width: 180,
          },
        ]
      : []),
  ]
}

export const lockedOpportunityColumns = ['contactWithOpportunityName']

export const defaultOppColVis = opportunityListColumns(
  [],
).reduce<GridColumnVisibilityModel>(
  (acc, { field }) => {
    if (acc[field] === undefined) {
      return { ...acc, [field]: true }
    } else return acc
  },
  {
    sourceId: false,
    campaignId: false,
    billToDivisionId: false,
    billToLocationTypeId: false,
    services: false,
    salesTeamId: false,
    branch: false,
    initialValue: false,
    recurringValue: false,
    referralLocationCode: false,
    referralDiscountCode: false,
    primaryContactSearchName: false,
    createdDate: true,
    leadCreatedTime: false,
    externalBillToCode: false,
    externalLocationCode: false,
    divisionId: false,
    locationTypeId: false,
    nextActionDate: false,
    'primaryContact/primaryEmailAddress': false,
    'primaryContact/phone': false,
    'primaryContact/address': false,
    soldServiceStatus: false,
    closedDate: false,
    type: false,
    closedLostReason: false,
    submittedBy: false,
    targetPestIds: false,
    billToAddress: false,
    customerReferralLocationCode: false,
    employeeReferral: false,
    otherReferral: false,
  },
)

export const defaultOpportunityColumns = opportunityListColumns([]).map(
  (c) => c.field,
)

export const opportunitySortConfig: SortConfig = {
  createdDate: { tiebreakers: ['id'] },
}
